/**
 * Resolve check-in/out for an employee on a calendar day (API + local worklog).
 */
import { attendanceService } from "../services/api";
import { readEmployeeWorklog } from "./attendancePunchStorage";

/** `YYYY-MM-DD` for &lt;input type="date" max="..." /&gt; (local today). */
export function getMaxDateInputValue(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function isFutureDateIso(dateIso, now = new Date()) {
  if (!dateIso || !/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) return false;
  return dateIso > getMaxDateInputValue(now);
}

export function clampDateIsoToToday(dateIso, now = new Date()) {
  const max = getMaxDateInputValue(now);
  if (!dateIso) return max;
  return dateIso > max ? max : dateIso;
}

/** Max `HH:mm` for &lt;input type="time" max="..." /&gt; on a given date (today → now, past → end of day). */
export function getMaxTimeInputValueForDate(dateIso, now = new Date()) {
  const effectiveDate = dateIso || getMaxDateInputValue(now);
  if (isFutureDateIso(effectiveDate, now)) return "00:00";
  if (effectiveDate < getMaxDateInputValue(now)) return "23:59";
  const h = now.getHours();
  const m = now.getMinutes();
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function dateTimeFromIsoAndTime(dateIso, timeHHmm) {
  if (!dateIso || !timeHHmm || !/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) return null;
  const m = String(timeHHmm).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const [y, mo, d] = dateIso.split("-").map(Number);
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d) || Number.isNaN(h) || Number.isNaN(min)) {
    return null;
  }
  return new Date(y, mo - 1, d, h, min, 0, 0);
}

export function isFutureDateTime(dateIso, timeHHmm, now = new Date()) {
  if (!dateIso || !timeHHmm) return false;
  if (isFutureDateIso(dateIso, now)) return true;
  const dt = dateTimeFromIsoAndTime(dateIso, timeHHmm);
  if (!dt || Number.isNaN(dt.getTime())) return false;
  return dt.getTime() > now.getTime();
}

/** Clamp a time value so it is not after "now" on the selected date. */
export function clampTimeInputToNow(dateIso, timeHHmm, now = new Date()) {
  if (!timeHHmm) return "";
  if (!isFutureDateTime(dateIso, timeHHmm, now)) return timeHHmm;
  return getMaxTimeInputValueForDate(dateIso, now);
}

/**
 * Validate regularise check-in/out (no future date or times).
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateRegulariseCheckTimes({ dateIso, inTime, outTime, now = new Date() }) {
  const errors = [];
  if (!dateIso) {
    errors.push("Select a date.");
  } else if (isFutureDateIso(dateIso, now)) {
    errors.push("Cannot regularise attendance for a future date.");
  }

  const inTrim = String(inTime || "").trim();
  const outTrim = String(outTime || "").trim();

  if (inTrim && isFutureDateTime(dateIso, inTrim, now)) {
    errors.push("Check-in time cannot be in the future.");
  }
  if (outTrim && isFutureDateTime(dateIso, outTrim, now)) {
    errors.push("Check-out time cannot be in the future.");
  }

  if (inTrim && outTrim && dateIso && !isFutureDateIso(dateIso, now)) {
    const inDt = dateTimeFromIsoAndTime(dateIso, inTrim);
    const outDt = dateTimeFromIsoAndTime(dateIso, outTrim);
    if (inDt && outDt && outDt.getTime() <= inDt.getTime()) {
      errors.push("Check-out must be after check-in on the same day.");
    }
  }

  return { valid: errors.length === 0, errors };
}

/** Logged-in user's linked employee id (navbar punch / ESS / regularise). */
export function getLoggedInEmployeeId() {
  try {
    const currentUser = JSON.parse(localStorage.getItem("currentUser") || "null");
    const id =
      currentUser?.employeeId ??
      currentUser?.employee?.id ??
      currentUser?.employee_id ??
      localStorage.getItem("hrms_current_employee_id");
    return id != null && id !== "" ? String(id) : null;
  } catch {
    return null;
  }
}

/**
 * Date picker change: block future dates and return a user-visible warning.
 */
export function applyRegulariseDateChange(rawDateIso, now = new Date()) {
  if (!rawDateIso) {
    return { dateIso: getMaxDateInputValue(now), error: null };
  }
  if (isFutureDateIso(rawDateIso, now)) {
    return {
      dateIso: clampDateIsoToToday(rawDateIso, now),
      error: "Cannot regularise attendance for a future date.",
    };
  }
  return { dateIso: rawDateIso, error: null };
}

/**
 * Time picker change: clamp to now on today and warn if user entered a future time.
 */
export function applyRegulariseTimeChange(dateIso, rawTime, fieldLabel = "Time", now = new Date()) {
  const trimmed = String(rawTime || "").trim();
  if (!trimmed) {
    return { time: "", error: null };
  }
  if (isFutureDateIso(dateIso, now)) {
    return { time: "", error: "Select a valid date (not in the future) first." };
  }
  const clamped = clampTimeInputToNow(dateIso, trimmed, now);
  if (isFutureDateTime(dateIso, trimmed, now) || clamped !== trimmed) {
    return {
      time: clamped,
      error: `${fieldLabel} cannot be in the future.`,
    };
  }
  return { time: clamped, error: null };
}

function formatClockFromMs(ms) {
  if (ms == null || !Number.isFinite(ms)) return null;
  return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatElapsedFromMs(inMs, outMs) {
  if (inMs == null || outMs == null || !Number.isFinite(inMs) || !Number.isFinite(outMs)) {
    return null;
  }
  const diff = Math.max(0, outMs - inMs);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function pickTimeField(record, keys) {
  if (!record) return null;
  for (const k of keys) {
    const v = record[k];
    if (v != null && v !== "" && v !== "--") return String(v).trim();
  }
  return null;
}

function recordMatchesDateIso(record, dateIso) {
  const raw = record?.date;
  if (!raw) return false;
  const s = String(raw).trim().slice(0, 10);
  return s === dateIso;
}

function applyApiRecordToResult(record, result) {
  const apiIn = pickTimeField(record, [
    "checkIn",
    "checkInTime",
    "inTime",
    "firstIn",
    "firstPunchIn",
  ]);
  const apiOut = pickTimeField(record, [
    "checkOut",
    "checkOutTime",
    "outTime",
    "lastOut",
    "lastPunchOut",
  ]);
  const apiHours = pickTimeField(record, [
    "totalHours",
    "workedHours",
    "workingHours",
    "totalWorkedHours",
  ]);
  if (!apiIn && !apiOut) return result;
  if (!result.sources.includes("system")) result.sources.push("system");
  if (apiIn) result.checkIn = apiIn;
  if (apiOut) result.checkOut = apiOut;
  if (apiHours) result.totalHours = apiHours;
  return result;
}

async function fetchHistoryRecord(employeeId, dateIso, preferMyHistory) {
  const [y, m] = dateIso.split("-").map(Number);
  const loggedInId = getLoggedInEmployeeId();
  const isSelf = loggedInId && String(loggedInId) === String(employeeId);

  const loaders = [];
  if (preferMyHistory || isSelf) {
    loaders.push(async () => {
      const response = await attendanceService.getMyAttendanceHistory(m, y);
      const records = Array.isArray(response?.data) ? response.data : [];
      return records.find((r) => recordMatchesDateIso(r, dateIso)) || null;
    });
  }
  loaders.push(async () => {
    const response = await attendanceService.getAttendanceHistory(m, y, 1, 500, "", [
      employeeId,
    ]);
    const records = Array.isArray(response?.data) ? response.data : [];
    return records.find((r) => recordMatchesDateIso(r, dateIso)) || null;
  });

  for (const load of loaders) {
    try {
      const record = await load();
      if (record) return record;
    } catch {
      /* try next source */
    }
  }
  return null;
}

/**
 * @param {string|number} employeeId
 * @param {string} dateIso - YYYY-MM-DD
 * @param {{ preferMyHistory?: boolean }} [options]
 * @returns {Promise<{ hasRecords: boolean, checkIn: string|null, checkOut: string|null, totalHours: string|null, sources: string[] }>}
 */
export async function resolveEmployeeDayChecks(employeeId, dateIso, options = {}) {
  if (!employeeId || !dateIso || !/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) {
    return { hasRecords: false, checkIn: null, checkOut: null, totalHours: null, sources: [] };
  }
  if (isFutureDateIso(dateIso)) {
    return { hasRecords: false, checkIn: null, checkOut: null, totalHours: null, sources: [] };
  }

  const sources = [];
  let checkIn = null;
  let checkOut = null;
  let totalHours = null;

  const worklog = readEmployeeWorklog(employeeId, dateIso);
  if (worklog?.inMs) {
    sources.push("punch");
    checkIn = formatClockFromMs(worklog.inMs);
    if (worklog.outMs != null) {
      checkOut = formatClockFromMs(worklog.outMs);
      totalHours = formatElapsedFromMs(worklog.inMs, worklog.outMs);
    }
  }

  let result = { checkIn, checkOut, totalHours, sources };
  try {
    const record = await fetchHistoryRecord(
      employeeId,
      dateIso,
      options.preferMyHistory === true
    );
    if (record) result = applyApiRecordToResult(record, result);
  } catch {
    /* API optional — local punch still shown */
  }

  checkIn = result.checkIn;
  checkOut = result.checkOut;
  totalHours = result.totalHours;

  const hasRecords =
    (checkIn != null && checkIn !== "") || (checkOut != null && checkOut !== "");

  return { hasRecords, checkIn, checkOut, totalHours, sources: result.sources };
}

export const regularisedChecksSessionKey = (employeeId) =>
  employeeId != null ? `hrms-calendar-regularised-checks-${String(employeeId)}` : null;

/** Duration label from two `HH:mm` values (same day). */
export function computeDurationBetweenTimeInputs(inVal, outVal) {
  const a = String(inVal || "").trim();
  const b = String(outVal || "").trim();
  if (!a || !b) return "";
  const parse = (t) => {
    const m = t.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return NaN;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  };
  let start = parse(a);
  let end = parse(b);
  if (Number.isNaN(start) || Number.isNaN(end)) return "";
  if (end < start) end += 24 * 60;
  const diff = end - start;
  if (diff <= 0) return "";
  const hh = Math.floor(diff / 60);
  const mm = diff % 60;
  return `${hh}h ${String(mm).padStart(2, "0")}m`;
}

/**
 * Append or replace a regularised day entry in session storage (for View history).
 */
export function saveRegularisationSessionEntry(employeeId, entry) {
  const key = regularisedChecksSessionKey(employeeId);
  if (!key || !entry) return;
  const day = Number(entry.day);
  const month = Number(entry.month);
  const year = Number(entry.year);
  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) {
    const parts = String(entry.dateIso || entry.date || "")
      .trim()
      .split("-");
    if (parts.length < 3) return;
    const y = parseInt(parts[0], 10);
    const mo = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) return;
    entry = { ...entry, year: y, month: mo, day: d };
  }
  const empId = String(employeeId);
  const label =
    entry.label ||
    `${String(entry.day).padStart(2, "0")}-${String(entry.month + 1).padStart(2, "0")}-${entry.year}`;
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(parsed) ? parsed : [];
    const filtered = list.filter(
      (e) =>
        !(
          String(e.employeeId) === empId &&
          e.year === entry.year &&
          e.month === entry.month &&
          e.day === entry.day
        )
    );
    const next = [
      {
        id: entry.id || `sess-${Date.now()}`,
        employeeId: empId,
        label,
        day: entry.day,
        year: entry.year,
        month: entry.month,
        checkIn: entry.checkIn ?? "--",
        checkOut: entry.checkOut ?? "--",
        totalHours: entry.totalHours ?? "--",
        reason: entry.reason || "",
        notes: entry.notes || "",
        savedAt: entry.savedAt || new Date().toISOString(),
      },
      ...filtered,
    ];
    sessionStorage.setItem(key, JSON.stringify(next));
    try {
      window.dispatchEvent(new CustomEvent("hrms-regularisation-history-updated", { detail: { employeeId: empId } }));
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore */
  }
}

/** One entry per calendar day (latest wins), newest first. */
export function loadRegularisationSessionHistory(employeeId) {
  const key = regularisedChecksSessionKey(employeeId);
  if (!key) return [];
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const empId = String(employeeId);
    const items = parsed.filter((e) => String(e.employeeId) === empId);
    const seen = new Map();
    for (const e of items) {
      const k = `${e.year}-${e.month}-${e.day}`;
      if (!seen.has(k)) seen.set(k, e);
    }
    return Array.from(seen.values()).sort((a, b) => {
      const ta = a.year * 400 + a.month * 32 + a.day;
      const tb = b.year * 400 + b.month * 32 + b.day;
      return tb - ta;
    });
  } catch {
    return [];
  }
}

/** Normalize display/API time to `HH:mm` for &lt;input type="time" /&gt;. */
export function toTimeInputValue(raw) {
  if (raw == null || raw === "" || raw === "--") return "";
  const s = String(raw).trim();
  const ampm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])$/);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const ap = ampm[3].toUpperCase();
    if (ap === "PM" && h < 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
  const hm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hm) {
    const h = Math.min(23, parseInt(hm[1], 10));
    const m = Math.min(59, parseInt(hm[2], 10));
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
  return "";
}
